import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { registerParallax } from '../../lib/living';
import { speciesFor } from '../../lib/species';
import { useSpeciesPhoto } from '../../lib/speciesPhoto';
import SpeciesArt from './SpeciesArt';

/* Holographic species tile: the real photograph when available, otherwise a
 * species-true 3D illustration (or the catalogue emoji for fruit & veg).
 * The foil layer shifts with device tilt / pointer like a holo trading card.
 */
// Photos already decoded this session skip the fade, so lists never blink.
const shown = new Set();

export default function SpeciesIcon({ kind, id, wiki, emoji, name = '', size = 56, height, rounded = 'rounded-2xl', photo = true, className = '' }) {
    // `wiki` looks up an ad-hoc species (e.g. an AI identification's Latin name)
    const sp = speciesFor(kind, id) || (wiki ? { w: wiki } : null);
    const ref = useRef(null);
    const img = useRef(null);
    const [visible, setVisible] = useState(false);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => registerParallax(ref.current), []);
    useEffect(() => {
        const el = ref.current;
        if (!el || visible) return undefined;
        if (!('IntersectionObserver' in window)) { setVisible(true); return undefined; }
        const io = new IntersectionObserver((entries) => {
            if (entries.some((e) => e.isIntersecting)) { setVisible(true); io.disconnect(); }
        }, { rootMargin: '900px 0px' }); // start well before it scrolls in
        io.observe(el);
        return () => io.disconnect();
    }, [visible]);

    const pic = useSpeciesPhoto(sp?.w, photo && visible);
    const src = pic ? (size > 120 ? pic.url : pic.thumb || pic.url) : null;

    useLayoutEffect(() => {
        const el = img.current;
        if (src && (shown.has(src) || (el?.complete && el.naturalWidth))) setLoaded(true);
    }, [src]);

    return (
        <span
            ref={ref}
            className={`holo-tile ${rounded} ${className}`}
            style={{ width: size, height: height ?? size }}
            data-testid={`species-icon-${kind || 'wiki'}-${id || wiki || 'x'}`}
        >
            <span className="holo-tile__art">
                {sp?.a ? (
                    <SpeciesArt look={sp.a} seed={id} className="w-full h-full" title={name} />
                ) : (
                    <span className="holo-tile__emoji" style={{ fontSize: Math.min(size, height ?? size) * 0.56 }} role="img" aria-label={name}>{emoji || '🌱'}</span>
                )}
            </span>
            {src && (
                <img
                    ref={img}
                    src={src}
                    alt={name}
                    loading="lazy"
                    decoding="async"
                    referrerPolicy="no-referrer"
                    onLoad={() => { shown.add(src); setLoaded(true); }}
                    className={`holo-tile__photo ${loaded ? 'is-loaded' : ''} ${shown.has(src) ? 'no-fade' : ''}`}
                />
            )}
            <span className="holo-foil" aria-hidden />
        </span>
    );
}
